import { gql, useMutation, useApolloClient } from '@apollo/client';
import { trackBookingStatusChanged } from '../util/tracking';

export const ACCEPT_BOOKING = gql`
  mutation AcceptBooking($input: AcceptBookingInput!) {
    acceptBooking(input: $input) {
      id
      studioID
      studioOwnerID
      userID
      startDateTime
      endDateTime
      totalPrice
      status
      projectName
      companyName
      projectDescription
      totalStudioPrice
      totalCreativePrice
      paymentStatus
      paymentIntentID
      guestReviewed
      hostReviewed
      Studio {
        id
        StudioName
        images
        studioLocation
        pricePerHour
        image
        description
        userID
        studioTypes
        studioSize
        numberOfPeople
        city
        postcode
        User {
          id
          firstName
          lastName
          profilePicture
        }
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
      }
      User {
        id
        firstName
        lastName
        profilePicture
      }
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const DECLINE_BOOKING = gql`
  mutation DeclineBooking($input: DeclineBookingInput!) {
    declineBooking(input: $input) {
      id
      studioID
      studioOwnerID
      userID
      startDateTime
      endDateTime
      totalPrice
      status
      projectName
      companyName
      projectDescription
      totalStudioPrice
      totalCreativePrice
      paymentStatus
      paymentIntentID
      declineReason
      cancellationReason
      cancelledAt
      cancelledBy
      guestReviewed
      hostReviewed
      Studio {
        id
        StudioName
        images
        studioLocation
        pricePerHour
        image
        description
        userID
        studioTypes
        studioSize
        numberOfPeople
        city
        postcode
        User {
          id
          firstName
          lastName
          profilePicture
        }
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
      }
      User {
        id
        firstName
        lastName
        profilePicture
      }
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const CANCEL_BOOKING = gql`
  mutation CancelBooking($input: CancelBookingInput!) {
    cancelBooking(input: $input) {
      id
      studioID
      studioOwnerID
      userID
      startDateTime
      endDateTime
      totalPrice
      status
      projectName
      companyName
      projectDescription
      totalStudioPrice
      totalCreativePrice
      paymentStatus
      paymentIntentID
      declineReason
      cancellationReason
      cancelledAt
      cancelledBy
      guestReviewed
      hostReviewed
      Studio {
        id
        StudioName
        images
        studioLocation
        pricePerHour
        image
        description
        userID
        studioTypes
        studioSize
        numberOfPeople
        city
        postcode
        User {
          id
          firstName
          lastName
          profilePicture
        }
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
      }
      User {
        id
        firstName
        lastName
        profilePicture
      }
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const WITHDRAW_BOOKING = gql`
  mutation WithdrawBooking($input: WithdrawBookingInput!) {
    withdrawBooking(input: $input) {
      id
      studioID
      studioOwnerID
      userID
      startDateTime
      endDateTime
      totalPrice
      status
      projectName
      companyName
      projectDescription
      totalStudioPrice
      totalCreativePrice
      paymentStatus
      paymentIntentID
      declineReason
      cancellationReason
      cancelledAt
      cancelledBy
      guestReviewed
      hostReviewed
      Studio {
        id
        StudioName
        images
        studioLocation
        pricePerHour
        image
        description
        userID
        studioTypes
        studioSize
        numberOfPeople
        city
        postcode
        User {
          id
          firstName
          lastName
          profilePicture
        }
        country
        approxLatitude
        approxLongitude
        locationLabel
        locationPrecision
        minNumberOfHours
      }
      User {
        id
        firstName
        lastName
        profilePicture
      }
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const useBookingUpdate = () => {
    const client = useApolloClient();
    
    const [acceptBookingMutation, { loading: acceptLoading }] = useMutation(ACCEPT_BOOKING);

    const [declineBookingMutation, { loading: declineLoading }] = useMutation(DECLINE_BOOKING);
    
    const [cancelBookingMutation, { loading: cancelLoading }] = useMutation(CANCEL_BOOKING);

    const [withdrawBookingMutation, { loading: withdrawLoading }] = useMutation(WITHDRAW_BOOKING);

    const updateBookingStatus = async (bookingId, newStatus, extraUpdates = {}) => {        
        if (!bookingId) {
            console.error('[useBookingUpdate] Error: Booking ID is required');
            throw new Error('Buchung konnte nicht zugeordnet werden. Bitte lade die Seite neu und versuche es erneut.');
        }

        if (!newStatus) {
            console.error('[useBookingUpdate] Error: New status is required');
            throw new Error('Der Buchungsstatus konnte nicht gesetzt werden. Bitte versuche es erneut.');
        }

        const variables = {
            input: {
                bookingID: bookingId,
                ...extraUpdates
            }
        };

        try {
            let data;
            
            if (newStatus === "CONFIRMED") {
                const result = await acceptBookingMutation({ variables });
                data = result.data?.acceptBooking;
            } else if (newStatus === "CANCELLED") {
                const result = await cancelBookingMutation({ variables });
                data = result.data?.cancelBooking;
            } else if (newStatus === "WITHDRAWN") {
                const result = await withdrawBookingMutation({ variables });
                data = result.data?.withdrawBooking;
            } else if (newStatus === "DECLINED" || newStatus === "REJECTED") {
                const result = await declineBookingMutation({ variables });
                data = result.data?.declineBooking;
            } else {
                console.error('[useBookingUpdate] Unsupported booking status:', newStatus);
                throw new Error('Der Buchungsstatus wird nicht unterstützt.');
            }

            // Refetch all observable queries to update the UI
            try {
                // Refetch only active queries that have observers
                await client.refetchQueries({ 
                    include: (query) => {
                        // Only refetch queries that are currently being observed
                        return query.hasObservers();
                    }
                });
            } catch (refetchError) {
                // Silently fail - the mutation was successful, UI will update on next navigation/refresh
            }

            trackBookingStatusChanged({
                booking: data,
                status: newStatus,
                source: 'booking_management'
            });

            return data;
        } catch (error) {
            throw error;
        }
    };

    // Keep updateBooking for backward compatibility, but route to appropriate mutation
    const updateBooking = async ({ id, status, ...updates }) => {
        
        if (!id) {
            throw new Error('Buchung konnte nicht zugeordnet werden. Bitte lade die Seite neu und versuche es erneut.');
        }

        if (!status) {
            throw new Error('Der Buchungsstatus konnte nicht gesetzt werden. Bitte versuche es erneut.');
        }

        return updateBookingStatus(id, status, updates);
    };

    const isLoading = acceptLoading || declineLoading || cancelLoading || withdrawLoading;

    return {
        updateBooking,
        updateBookingStatus,
        isLoading
    };
};
